import React, { useEffect, useRef, useState } from "react";
import {
  useStartAgenticTraceExportMutation,
  useGetAgenticTraceExportJobQuery,
} from "../../../app/api/console/agenticRunsApiSlice";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:8020";

function authHeaders() {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// Preferred path: the backend uploaded the zip to S3 — navigate straight to
// the presigned URL (S3-direct, no dyno proxying). Falls back to an
// authenticated blob fetch through the dyno when S3 upload was unavailable.
async function downloadZip(jobId, runId) {
  const urlResp = await fetch(
    `${API_BASE}/agentic-runs/trace-export-jobs/${jobId}/download-url`,
    { credentials: "include", headers: authHeaders() }
  );
  if (urlResp.ok) {
    const { url } = await urlResp.json();
    const a = document.createElement("a");
    a.href = url;
    a.download = `trace-${runId}.zip`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    return;
  }
  const resp = await fetch(
    `${API_BASE}/agentic-runs/trace-export-jobs/${jobId}/download`,
    { credentials: "include", headers: authHeaders() }
  );
  if (!resp.ok) {
    throw new Error(`download failed (${resp.status})`);
  }
  const blob = await resp.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `trace-${runId}.zip`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

const PHASE_LABELS = {
  queued: "Queued",
  resolve: "Resolving run",
  events: "Pulling events",
  orchestrator: "Orchestrator artifacts",
  "fanout-artifacts": "Fan-out artifacts",
  journeys: "Journey results",
  images: "Downloading images",
  context: "Context files",
  platform: "Platform view",
  render: "Rendering timelines",
  zip: "Zipping",
  done: "Ready",
};

function formatEta(seconds) {
  if (seconds == null) return null;
  if (seconds < 5) return "almost done";
  if (seconds < 60) return `~${Math.round(seconds)}s left`;
  return `~${Math.round(seconds / 60)} min left`;
}

/**
 * "Export trace" button for the control-tower run page: kicks off the
 * backend's full-trace export job, polls it with a determinate progress bar
 * (percent + ETA computed server-side from phase weights), then downloads
 * the zip — S3-direct via presigned URL when available.
 */
function TraceExportButton({ env, runId }) {
  const [jobId, setJobId] = useState(null);
  const [phase, setPhase] = useState("idle"); // idle | preparing | downloading | done | error
  const [error, setError] = useState(null);
  const [startExport, { isLoading: isStarting }] =
    useStartAgenticTraceExportMutation();
  const downloadStarted = useRef(false);

  const { data: job } = useGetAgenticTraceExportJobQuery(jobId, {
    skip: !jobId,
    pollingInterval: 2000,
  });

  useEffect(() => {
    if (!job) return;
    if (job.status === "error") {
      setPhase("error");
      setError(job.error || "export failed");
    } else if (job.status === "ready" && !downloadStarted.current) {
      downloadStarted.current = true;
      setPhase("downloading");
      downloadZip(job.job_id, runId)
        .then(() => setPhase("done"))
        .catch((e) => {
          setPhase("error");
          setError(e.message);
        });
    }
  }, [job, runId]);

  const handleClick = async () => {
    setError(null);
    setPhase("preparing");
    downloadStarted.current = false;
    try {
      const started = await startExport({ env, runId }).unwrap();
      setJobId(started.job_id);
    } catch (e) {
      setPhase("error");
      setError(e?.data?.detail || "failed to start export");
    }
  };

  const busy = phase === "preparing" || phase === "downloading" || isStarting;
  const jobPhaseLabel = job?.phase ? PHASE_LABELS[job.phase] || job.phase : null;
  const percent = phase === "preparing" ? job?.percent : null;
  const eta = formatEta(job?.eta_seconds);

  let label = "Export trace";
  if (isStarting || (phase === "preparing" && !job)) label = "Starting…";
  else if (phase === "preparing")
    label = jobPhaseLabel ? `${jobPhaseLabel}…` : "Preparing…";
  else if (phase === "downloading")
    label = job?.size_bytes
      ? `Downloading ${(job.size_bytes / 1e6).toFixed(0)} MB…`
      : "Downloading…";
  else if (phase === "done") label = "Export again";
  else if (phase === "error") label = "Retry export";

  return (
    <span className='inline-flex flex-col gap-1' title={error || undefined}>
      <span className='inline-flex items-center gap-2'>
        <button
          onClick={handleClick}
          disabled={busy}
          title={
            phase === "error"
              ? error
              : "Download the full agent trace (orchestrator, workers, subagents, issues, images) as a zip. Takes a few minutes to prepare."
          }
          className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg transition-colors ${
            phase === "error"
              ? "text-red-700 bg-red-50 border border-red-200 hover:bg-red-100"
              : "text-indigo-700 bg-indigo-50 border border-indigo-200 hover:bg-indigo-100"
          } ${busy ? "opacity-70 cursor-wait" : ""}`}>
          {busy && (
            <svg
              className='w-3.5 h-3.5 animate-spin'
              fill='none'
              viewBox='0 0 24 24'
              stroke='currentColor'
              strokeWidth={2}>
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                d='M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15'
              />
            </svg>
          )}
          {!busy && (
            <svg
              className='w-3.5 h-3.5'
              fill='none'
              viewBox='0 0 24 24'
              stroke='currentColor'
              strokeWidth={2}>
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                d='M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M12 4v12m0 0l-4-4m4 4l4-4'
              />
            </svg>
          )}
          {label}
        </button>
        {phase === "error" && error && (
          <span className='text-[11px] text-red-500 max-w-[220px] truncate'>{error}</span>
        )}
      </span>
      {phase === "preparing" && percent != null && (
        <span className='flex items-center gap-2 w-56'>
          <span className='flex-1 h-1.5 rounded-full bg-gray-200 overflow-hidden'>
            <span
              className='block h-full rounded-full bg-indigo-500 transition-all duration-700'
              style={{ width: `${Math.max(2, percent)}%` }}
            />
          </span>
          <span className='text-[10px] text-gray-500 whitespace-nowrap'>
            {Math.round(percent)}%{eta ? ` · ${eta}` : ""}
          </span>
        </span>
      )}
    </span>
  );
}

export default TraceExportButton;
